import clsx from 'clsx';
import { getMilestoneLabel } from '../../utils/vacationHistoryMilestones';
import type { HistoryRequestMilestone, HistoryEventKind } from '../../models/vacationHistoryReport.types';

interface HistoryMilestonePathProps {
    milestones: HistoryRequestMilestone[];
    highlightedKind?: HistoryEventKind;
}

interface DisplayMilestone {
    key: string;
    label: string;
    startDate: Date | null;
    endDate: Date | null;
    kind: HistoryEventKind | 'segment';
    segmentOrder?: number;
    isRejected: boolean;
    isProjected: boolean;
}

export function HistoryMilestonePath({
    milestones,
    highlightedKind,
}: HistoryMilestonePathProps) {
    const displayMilestones: DisplayMilestone[] = [];

    const baseOrder = ['requested', 'managerApproved', 'clientApproved', 'dpApproved'];
    baseOrder.forEach(kind => {
        const m = milestones.find(x => x.kind === kind);
        if (m) {
            displayMilestones.push({
                key: kind,
                label: getMilestoneLabel(kind as HistoryEventKind),
                startDate: m.occurredAt,
                endDate: null,
                kind: kind as HistoryEventKind,
                isRejected: false,
                isProjected: false,
            });
        }
    });

    const segmentMap = new Map<number, { started: HistoryRequestMilestone | null; completed: HistoryRequestMilestone | null }>();
    milestones.forEach(m => {
        if ((m.kind === 'started' || m.kind === 'completed') && m.segmentOrder) {
            if (!segmentMap.has(m.segmentOrder)) {
                segmentMap.set(m.segmentOrder, { started: null, completed: null });
            }
            const seg = segmentMap.get(m.segmentOrder)!;
            if (m.kind === 'started') seg.started = m;
            else seg.completed = m;
        }
    });

    Array.from(segmentMap.entries()).forEach(([order, { started, completed }]) => {
        const label = order === 1 && segmentMap.size === 1 ? 'Férias' : `${['1ª', '2ª', '3ª'][order - 1] || `${order}ª`} parcela`;
        displayMilestones.push({
            key: `segment-${order}`,
            label,
            startDate: started?.occurredAt || null,
            endDate: completed?.occurredAt || null,
            kind: 'segment',
            segmentOrder: order,
            isRejected: false,
            isProjected: started?.isProjected ?? false,
        });
    });

    const rejected = milestones.find(m => m.kind === 'rejected');
    if (rejected) {
        displayMilestones.push({
            key: 'rejected',
            label: 'Recusada',
            startDate: rejected.occurredAt,
            endDate: null,
            kind: 'rejected',
            isRejected: true,
            isProjected: false,
        });
    }

    if (displayMilestones.length === 0) {
        return <div className="text-sm text-gray-500">Nenhuma movimentação registrada</div>;
    }

    return (
        <div className="flex items-center gap-2 overflow-x-auto px-2 py-3">
            {displayMilestones.map((milestone, idx) => {
                const isHighlighted =
                    (milestone.kind !== 'segment' && milestone.kind === highlightedKind) ||
                    (milestone.kind === 'segment' && (highlightedKind === 'started' || highlightedKind === 'completed'));
                const isRejected = milestone.isRejected;
                const isProjected = milestone.isProjected;

                const dotClass = clsx(
                    'h-4 w-4 rounded-full transition-all shrink-0',
                    isHighlighted && 'ring-2 ring-primary-400 ring-offset-2',
                    isRejected && 'bg-red-500',
                    isProjected && !isRejected && 'bg-gray-400',
                    !isProjected && !isRejected && 'bg-green-500'
                );

                const dateStr = milestone.startDate
                    ? milestone.startDate.toLocaleDateString('pt-BR', { month: '2-digit', day: '2-digit' })
                    : null;
                const endDateStr = milestone.endDate
                    ? milestone.endDate.toLocaleDateString('pt-BR', { month: '2-digit', day: '2-digit' })
                    : null;
                const dateRange = dateStr && endDateStr ? `${dateStr} → ${endDateStr}` : dateStr;

                return (
                    <div key={milestone.key} className="flex flex-col items-center gap-1.5 shrink-0">
                        <div className={dotClass} />
                        <div className="text-xs text-center text-raro-gray-600 min-w-[70px]">
                            {dateRange && <p className="font-medium">{dateRange}</p>}
                            <p className="text-raro-gray-500 break-words leading-tight">{milestone.label}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
